import React, { useState, useMemo } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Box,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import { motion } from 'framer-motion';
import LaunchIcon from '@mui/icons-material/Launch';
import LockIcon from '@mui/icons-material/Lock';
import StarIcon from '@mui/icons-material/Star';
import { alpha } from '@mui/material/styles';

// Define Main Projects and Mini Projects clearly
const mainProjects = [
  {
    title: 'AI Transformer Fault Detection — K-Electric',
    subtitle: 'Final Year Project · Production Deployment',
    description:
      'Trained a YOLOv8 CNN to detect 7 transformer defect classes (dust, rust, oil leakage, holes, broken parts, burn marks, bloating) for K-Electric field engineers. Built a custom image labeling + augmentation pipeline, achieved production-grade real-time bounding-box inference, and exposed the model via FastAPI — integrated directly into K-Electric\'s live infrastructure.',
    tags: ['YOLOv8', 'TensorFlow', 'FastAPI', 'Python', 'OpenCV', 'CNN'],
    link: null,
    color: '#f97316',
    featured: true,
    badge: 'FYP',
    images: ['/Images/transformer%201.png', '/Images/transformer%202.png'],
    category: 'main',
  },
  {
    title: 'Enterprise Staffing CRM & ATS',
    subtitle: 'Live · Moghal Industries · Sole Developer',
    description:
      'Production-grade multi-office Staffing CRM modeled after Avionté — serving Admin, Front Office, Back Office, and Client Office roles. Features RBAC auth with JWT, 25+ REST API endpoints, ATS recruitment lifecycle (job posting → interviews → hiring pipelines → contracts), timesheet with CSV import, invoice engine with auto Bill/Pay/Markup, Azure Blob document storage, e-signature, and an AI resume generator. Deployed on Vercel + Railway with PostgreSQL on Neon (Azure).',
    tags: ['Node.js', 'TypeScript', 'React', 'PostgreSQL', 'Prisma ORM', 'Azure', 'RBAC'],
    link: null,
    color: '#8b5cf6',
    featured: true,
    badge: 'LIVE',
    images: ['/Images/crm%201.png', '/Images/crm%202.png'],
    category: 'main',
  },
  {
    title: 'Inventory Management System',
    subtitle: 'NED University (CSIT Dept.) · Live on Vercel',
    description:
      'University-wide inventory system built as Backend Developer at NED\'s CSIT Department. Led all backend development with Django REST Framework — RESTful APIs for smooth frontend integration, JWT authentication, role-based authorization, pagination for scalable data access, and MVC architecture. Deployed live on Vercel.',
    tags: ['Django', 'DRF', 'PostgreSQL', 'React', 'JWT', 'RBAC'],
    link: 'https://ned-inventory-management-system.vercel.app/',
    linkLabel: 'View Live App',
    color: '#6366f1',
    badge: 'LIVE',
    images: ['/Images/Inventory%201%20.png', '/Images/inventory%202.png'],
    category: 'main',
  },
  {
    title: 'LMS Platform with AI SQL Chatbot',
    subtitle: 'Full-Stack · Live on Railway',
    description:
      'Full Learning Management System with course enrollment, assignments, and progress tracking — deployed live on Railway with Django REST Framework + React + PostgreSQL. Integrated an n8n chatbot that converts natural language to SQL, enabling zero-code query execution for non-technical admins across the entire LMS database.',
    tags: ['Django', 'DRF', 'React', 'PostgreSQL', 'n8n', 'JWT'],
    link: 'https://github.com/saad9797/LMS',
    color: '#10b981',
    badge: 'LIVE',
    category: 'main',
  },
  {
    title: 'AI-Powered LMS Data Warehouse',
    subtitle: 'ETL · Power BI · RAG',
    description:
      'Built ETL pipelines consolidating OLTP PostgreSQL + MongoDB sources into a PostgreSQL star-schema data warehouse for optimized KPI queries. Delivered Power BI dashboards surfacing revenue and engagement insights. Implemented a Gemini-powered RAG system enabling natural-language SQL querying over platform KPIs.',
    tags: ['ETL', 'Power BI', 'RAG', 'PostgreSQL', 'MongoDB', 'Gemini'],
    link: 'https://github.com/saad9797/Edu-Hub',
    color: '#06b6d4',
    category: 'main',
  },
  {
    title: 'Hybrid RAG Chatbot',
    subtitle: 'n8n · Supabase · pgvector · Gemini',
    description:
      'Plug-and-play document intelligence pipeline: ingestion → vector embedding → smart SQL vs. semantic routing via Supabase pgvector and Gemini embeddings. Supports Google Drive as a document source with zero-code deployment via n8n workflows.',
    tags: ['n8n', 'RAG', 'Supabase', 'pgvector', 'Gemini', 'PostgreSQL'],
    link: 'https://github.com/saad9797/CCP-NLP',
    color: '#6366f1',
    category: 'main',
  },
  {
    title: 'Contact Manager App',
    subtitle: 'MERN Stack',
    description:
      'A full-stack MERN application for managing contacts following industry-standard development practices. Features user authentication, CRUD operations for contacts, and a responsive interface built for real-world usage.',
    tags: ['MongoDB', 'Express', 'React', 'Node.js'],
    link: 'https://github.com/saad9797/Contact-Manager-App',
    color: '#f59e0b',
    category: 'main',
  },
  {
    title: 'Airline Reservation System',
    subtitle: 'Backend · CI/CD',
    description:
      'A backend system for managing airline operations with testing and deployment pipelines. Created using Django, allowing management of flights, passengers, and airports. Implemented GitHub Actions for CI/CD.',
    tags: ['Django', 'CI/CD', 'GitHub Actions'],
    link: 'https://github.com/saad9797/Airline-Reservation-System',
    color: '#8b5cf6',
    category: 'main',
  },
];

const miniProjects = [
  {
    title: 'Tic-Tac-Toe AI',
    subtitle: 'Minimax & Alpha-Beta Pruning',
    description:
      'Implemented a Tic-Tac-Toe AI using the Minimax algorithm optimized with Alpha-Beta pruning. Designed a modular Python implementation with a CLI for human vs. AI gameplay.',
    tags: ['Python', 'AI', 'Algorithms'],
    link: 'https://github.com/saad9797/Tic_Tac_Toe-Minimax-AlphaBetaPruning-',
    color: '#6366f1',
    category: 'mini',
  },
  {
    title: 'Maze Solver',
    subtitle: 'Pathfinding Algorithms',
    description:
      'Developed a maze-solving program using A* (with Manhattan distance heuristic), BFS, and DFS to find optimal paths. Parsed maze layouts from txt files and visualized paths using matplotlib or terminal output.',
    tags: ['Python', 'A*', 'BFS', 'DFS'],
    link: 'https://github.com/saad9797/Maze-Game',
    color: '#06b6d4',
    category: 'mini',
  },
  {
    title: 'Animated Website Clone',
    subtitle: 'Significo Design',
    description:
      'An animated website mimicking the Significo design with modern front-end technologies. Developed using HTML, CSS, JavaScript, and GSAP for smooth animations.',
    tags: ['HTML', 'CSS', 'GSAP', 'JavaScript'],
    link: 'https://saad9797.github.io',
    color: '#10b981',
    category: 'mini',
  },
  {
    title: 'Naats App',
    subtitle: 'Audio Player',
    description:
      'A simple neat player app for playing and managing Naats. Built with HTML, CSS, and JavaScript, featuring a playlist to switch between Naats.',
    tags: ['HTML', 'CSS', 'JavaScript'],
    link: 'https://github.com/saad9797/player-version-2',
    color: '#f59e0b',
    category: 'mini',
  },
  {
    title: 'Notes App',
    subtitle: 'Local Storage',
    description:
      'A minimalist app for note-taking with local data persistence. Designed with HTML, CSS, and JavaScript, supporting note addition and deletion with local storage.',
    tags: ['HTML', 'CSS', 'JavaScript'],
    link: 'https://github.com/saad9797/project1',
    color: '#8b5cf6',
    category: 'mini',
  },
  {
    title: 'Image Search Engine',
    subtitle: 'Photo Browser',
    description:
      'A lightweight image search tool for browsing photos by keyword. Developed using HTML, CSS, and JavaScript, offering functionality similar to Pinterest.',
    tags: ['HTML', 'CSS', 'JavaScript', 'API'],
    link: 'https://github.com/saad9797/Small-Search-Engine',
    color: '#ec4899',
    category: 'mini',
  },
];

// Combine all projects for easier filtering
const allProjects = [...mainProjects, ...miniProjects];

const tabs = [
  { label: 'Main Projects', filter: 'main' },
  { label: 'Mini Projects', filter: 'mini' },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

const ProjectCard = React.memo(({ project }) => {
  const [activeImg, setActiveImg] = useState(0);
  return (
  <motion.div variants={cardVariants}>
    <Card
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: { xs: 2.5, md: 3 },
        borderRadius: 3,
        background: (theme) => alpha(theme.palette.background.paper, 0.6),
        backdropFilter: 'blur(12px)',
        border: (theme) =>
          project.featured
            ? `1.5px solid ${alpha(project.color, 0.35)}`
            : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-8px)',
          borderColor: (theme) => alpha(project.color, 0.35),
          boxShadow: (theme) =>
            `0 20px 25px -5px ${alpha('#000000', 0.25)}, 0 0 20px ${alpha(project.color, 0.15)}`,
          '&::before': {
            height: '4px',
            opacity: 1,
          },
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: project.featured ? '4px' : '2px',
          background: `linear-gradient(90deg, ${project.color}, ${alpha(project.color, 0.3)})`,
          opacity: project.featured ? 1 : 0.6,
          transition: 'all 0.3s ease',
        },
      }}
    >
      <CardContent sx={{ p: 0, flex: 1 }}>
        {/* Image Gallery */}
        {project.images && project.images.length > 0 && (
          <Box sx={{ mb: 2.5 }}>
            {/* Wrapper keeps a fixed aspect ratio so full image is always visible */}
            <Box
              sx={{
                width: '100%',
                aspectRatio: '16 / 9',
                borderRadius: 2,
                overflow: 'hidden',
                border: `1px solid ${alpha(project.color, 0.25)}`,
                boxShadow: `0 4px 16px ${alpha(project.color, 0.12)}`,
                background: (theme) => `linear-gradient(135deg, ${alpha(theme.palette.background.default, 0.9)}, ${alpha(project.color, 0.06)})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
              }}
            >
              <Box
                component="img"
                src={project.images[activeImg]}
                alt={`${project.title} screenshot ${activeImg + 1}`}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block',
                  transition: 'opacity 0.25s ease',
                }}
              />
            </Box>
            {project.images.length > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.75, mt: 1 }}>
                {project.images.map((_, i) => (
                  <Box
                    key={i}
                    onClick={() => setActiveImg(i)}
                    sx={{
                      width: activeImg === i ? 18 : 6,
                      height: 6,
                      borderRadius: 3,
                      bgcolor: activeImg === i ? project.color : alpha(project.color, 0.3),
                      cursor: 'pointer',
                      transition: 'all 0.25s ease',
                      '&:hover': { bgcolor: alpha(project.color, 0.7) },
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Featured badge + status badge row */}
        {(project.featured || project.badge) && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
            {project.featured && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.6,
                  px: 1.25,
                  py: 0.4,
                  borderRadius: '999px',
                  background: (theme) => alpha(project.color, 0.12),
                  border: `1px solid ${alpha(project.color, 0.3)}`,
                }}
              >
                <StarIcon sx={{ fontSize: '0.75rem', color: project.color }} />
                <Typography variant="caption" sx={{ color: project.color, fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  Featured
                </Typography>
              </Box>
            )}
            {project.badge && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  px: 1.25,
                  py: 0.4,
                  borderRadius: '999px',
                  background: project.badge === 'FYP'
                    ? 'rgba(249,115,22,0.12)'
                    : project.badge === 'LIVE'
                    ? 'rgba(16,185,129,0.1)'
                    : 'rgba(99,102,241,0.1)',
                  border: project.badge === 'FYP'
                    ? '1px solid rgba(249,115,22,0.35)'
                    : project.badge === 'LIVE'
                    ? '1px solid rgba(16,185,129,0.3)'
                    : '1px solid rgba(99,102,241,0.3)',
                }}
              >
                {project.badge === 'LIVE' && (
                  <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#10b981', mr: 0.6, boxShadow: '0 0 6px rgba(16,185,129,0.8)', animation: 'livePulse 2s ease-in-out infinite', '@keyframes livePulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.2 } } }} />
                )}
                <Typography variant="caption" sx={{ color: project.badge === 'FYP' ? '#f97316' : project.badge === 'LIVE' ? '#10b981' : '#818cf8', fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.08em' }}>
                  {project.badge}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Project Header */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              mb: 0.5,
              lineHeight: 1.3,
            }}
          >
            {project.title}
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 500,
              color: 'primary.main',
              fontSize: '0.875rem',
            }}
          >
            {project.subtitle}
          </Typography>
        </Box>

        {/* Description */}
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            lineHeight: 1.7,
            mb: 2.5,
            fontSize: '0.9375rem',
          }}
        >
          {project.description}
        </Typography>

        {/* Tags */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
          {project.tags.map((tag, tagIndex) => (
            <Chip
              key={tagIndex}
              label={tag}
              size="small"
              sx={{
                height: '24px',
                fontSize: '0.75rem',
                fontWeight: 600,
                backgroundColor: (theme) => alpha(project.color, 0.1),
                color: project.color,
                border: `1px solid ${alpha(project.color, 0.2)}`,
                '&:hover': {
                  backgroundColor: (theme) => alpha(project.color, 0.15),
                },
              }}
            />
          ))}
        </Box>

        {/* Action Button */}
        <Box
          sx={{
            pt: 2,
            mt: 'auto',
            borderTop: (theme) => `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          }}
        >
          {project.link ? (
            <Button
              href={project.link}
              data-cursor="hover"
              target="_blank"
              rel="noopener noreferrer"
              size="medium"
              fullWidth
              endIcon={<LaunchIcon sx={{ fontSize: '1.125rem' }} />}
              variant="contained"
              sx={{
                fontWeight: 600,
                fontSize: '0.9375rem',
                textTransform: 'none',
                py: 1.25,
                background: `linear-gradient(135deg, ${project.color}, ${alpha(project.color, 0.8)})`,
                boxShadow: `0 4px 12px ${alpha(project.color, 0.3)}`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${alpha(project.color, 0.9)}, ${project.color})`,
                  boxShadow: `0 6px 16px ${alpha(project.color, 0.4)}`,
                },
              }}
            >
              {project.linkLabel || 'View Code'}
            </Button>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 1.25,
                px: 2,
                borderRadius: 2,
                background: (theme) => alpha(theme.palette.background.paper, 0.5),
                border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <LockIcon sx={{ fontSize: '1rem', color: 'text.disabled' }} />
              <Typography variant="body2" sx={{ color: 'text.disabled', fontWeight: 500, fontSize: '0.875rem' }}>
                Private / Client Project
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  </motion.div>
  );
});

function Projects() {
  // Initialize to Main Projects (index 0)
  const [activeTab, setActiveTab] = useState(0); 

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const filteredProjects = useMemo(() => {
    const filterValue = tabs[activeTab].filter;
    return allProjects.filter((p) => p.category === filterValue);
  }, [activeTab]);

  return (
    <Container id="projects" maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="overline"
            sx={{
              color: 'primary.main',
              fontWeight: 700,
              letterSpacing: '0.15em',
              mb: 1,
              display: 'block',
            }}
          >
            PORTFOLIO
          </Typography>
          <Typography
            variant="h2"
            sx={{
              mb: 2,
              fontWeight: 700,
              background: 'linear-gradient(135deg, #e2e8f0 0%, #a1a1aa 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            My Projects
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              maxWidth: '600px',
              mx: 'auto',
              fontSize: '1.0625rem',
            }}
          >
            A collection of my work, categorized into main development projects and smaller experiments.
          </Typography>
        </Box>
        
        {/* Filter Tabs (Main and Mini) */}
        <Box sx={{ mb: 6, display: 'flex', justifyContent: 'center' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: 'primary.main',
                height: '3px',
                borderRadius: '3px 3px 0 0',
              },
              maxWidth: '100%',
              '& .MuiTabs-flexContainer': {
                justifyContent: 'center',
              },
            }}
          >
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                label={tab.label}
                data-cursor="hover"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.9375rem',
                  textTransform: 'none',
                  color: 'text.secondary',
                  px: { xs: 1.5, sm: 3 },
                  '&.Mui-selected': {
                    color: 'primary.main',
                  },
                }}
              />
            ))}
          </Tabs>
        </Box>

        {/* Projects Grid */}
        <motion.div
          key={activeTab} // Key changes on tab change, forcing re-render and re-animation
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {filteredProjects.length > 0 ? (
            <Grid container spacing={{ xs: 3, md: 4 }}>
              {filteredProjects.map((project, index) => (
                <Grid item xs={12} sm={6} lg={4} key={index}>
                  <ProjectCard project={project} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <Typography variant="h5" color="text.primary">
                No projects found in this category.
              </Typography>
            </Box>
          )}
        </motion.div>

        {/* Bottom Accent */}
        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          whileInView={{ opacity: 1, scaleX: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <Box
            sx={{
              mt: 8,
              height: '2px',
              background:
                'linear-gradient(90deg, transparent, #6366f1, #06b6d4, transparent)',
              borderRadius: 1,
              opacity: 0.3,
            }}
          />
        </motion.div>
      </motion.div>
    </Container>
  );
}

export default Projects;